<template>
    <central-modal
        :header="`Contract Details: ${props.contractId}`"
        v-model:visible="visible"
        size="large"
        hide-header
        @hide="onModalHide"
    >
        <template #header>
            <div class="epex-header">
                <h4>Contract Details: {{ props.contractId }}</h4>
                <div v-if="contract" class="metric-holder">
                    <tag :value="contract.contractType" :style="`background-color: ${contract.contractColour}; color: black;`" />
                    <tag :value="deliveryDate" :style="`background-color: ${contract.contractColour}; color: black;`" />
                    <tag :value="contract.deliveryBlock || contract.contractName" :style="`background-color: ${contract.contractColour}; color: black;`" />
                </div>
            </div>
        </template>
        <div class="contracts-data" v-if="contract">
            <div v-if="!contract.noData" class="data-holder">
                <div v-if="tradeChartConfig" class="trade-chart-shell grid-row-cell">
                    <module-chart-with-options
                        :chart-config="tradeChartConfig"
                        :chart-data="contract.chartData"
                        :blocked-actions="NON_USER_DASHBOARD_BLOCKED_ACTIONS"
                        hide-date
                        no-pushes
                        is-modal
                        display-options-top
                    />
                </div>
                <div class="trade-table table-shell grid-row-cell">
                    <div class="trade-table">
                        <generic-table
                            user-table-filter-id="epexContractTrade"
                            no-data-found-message="No Trades on Contract"
                            :data="contract.tradeTable"
                            hideTopFilters
                        />
                    </div>
                </div>
                <div class="trade-metrics table-shell grid-row-cell">
                    <metric-with-value
                        v-for="(item, key) in contract.metrics.data"
                        :key="key"
                        :metric="item.name"
                        :value="item.value === null ? 'N/A' : `${currencySymbol}${item.value}/MWh`"
                    />
                </div>
                <div class="bids grid-row-cell">
                    <div class="bids-title title">
                        Bids
                    </div>
                    <div class="bids-table table-shell">
                        <generic-table
                            user-table-filter-id="epexContractBid"
                            no-data-found-message="No Bids on Contract"
                            :data="contract.bidTable"
                            hideTopFilters
                        />
                    </div>
                </div>
                <div class="asks grid-row-cell">
                    <div class="asks-title title">
                        Asks
                    </div>
                    <div class="asks-table table-shell">
                        <generic-table
                            user-table-filter-id="epexContractAsk"
                            no-data-found-message="No Asks on Contract"
                            :data="contract.askTable"
                            hideTopFilters
                        />
                    </div>
                </div>
            </div>
            <div v-else class="no-data">
                No data currently available
            </div>
        </div>
        <div v-else-if="isLoading" class="loader">
            <component-loader />
        </div>
        <div v-else class="no-data">
            There was an issue loading this contract. Please try again later.
        </div>
    </central-modal>
</template>

<script lang="ts" setup>
import { frontendLogApi } from '@/api/powerMarkets/frontendLogApi.ts';
import { ITradeContract } from '@/interfaces/powerMarkets/epex/ITradeContract.ts';
import Tag from 'primevue/tag';
import {
    computed, nextTick, onUnmounted, ref, watch,
} from 'vue';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { IModuleChartConfig } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import MetricWithValue from '@/components/common/MetricWithValue.vue';
import { useSignalRLifecycle } from '@/realTimeConnection/SignalRLifecycle.ts';
import { epexApi } from '@/api/powerMarkets/epexApi.ts';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import { ITableDataConfig } from '@/interfaces/powerMarkets/table/ITableDataConfig.ts';
import { useToast } from 'primevue/usetoast';
import CentralModal from '@/components/common/CentralModal.vue';
import ModuleChartWithOptions from '@/components/powerMarkets/chart/ModuleChartWithOptions.vue';
import { NON_USER_DASHBOARD_BLOCKED_ACTIONS } from '@/interfaces/actions/enact/IModuleActions.ts';
import GenericTable from '../tables/GenericTable.vue';

dayjs.extend(advancedFormat);
const toast = useToast();
const signalRLifecycle = useSignalRLifecycle();

enum PushTypes {
    Refresh,
    Trade,
    BidAsk,
}

enum RequestKey {
    Initial = 'initial',
    Trade = 'trade',
    BidAsk = 'bidAsk',
}

interface RequestState {
    pending: boolean;
    rerun: boolean;
}

interface IContractMetrics {
    data: Array<{ name: string; value: string }>;
}

const visible = defineModel<boolean>();

const props = defineProps<{
    contractId: number;
    countryId: string;
}>();

const emit = defineEmits<{
    'hide': [];
}>();

const deliveryDate = ref<string>('');
const contract = ref<ITradeContract | null>(null);
const isLoading = ref<boolean>(false);
const tradeChartConfig = ref<IModuleChartConfig | null>(null);
const isAlive = ref<boolean>(true);

const requestState = ref<Record<RequestKey, RequestState>>({
    [RequestKey.Initial]: { pending: false, rerun: false },
    [RequestKey.Trade]: { pending: false, rerun: false },
    [RequestKey.BidAsk]: { pending: false, rerun: false },
});

const currencySymbol = computed<string>(() => (props.countryId === 'Gb' ? '£' : '€'));

function onModalHide (): void {
    emit('hide');
}

function setDeliveryDate (): void {
    if (!contract.value) return;
    const date = dayjs(contract.value.deliveryDate);
    deliveryDate.value = date.format('Do MMM YYYY');
}

function setModuleChartConfig (chartConfig: ITableDataConfig): void {
    if (!contract.value) return;
    tradeChartConfig.value = {
        data: chartConfig,
        info: {
            id: '',
            name: 'Trade Prices and Volumes',
            date: {
                from: { type: 'date', value: new Date() },
                to: { type: 'date', value: new Date() },
            },
        },
    };
}

const refreshHandler = (id: number): Promise<void> => handleSignalRPush(PushTypes.Refresh, id);
const tradeHandler = (): Promise<void> => handleSignalRPush(PushTypes.Trade);
const bidAskHandler = (): Promise<void> => handleSignalRPush(PushTypes.BidAsk);

function getMetricsWithFallback (metrics: IContractMetrics | null | undefined): IContractMetrics {
    return metrics?.data ? metrics : { data: [] };
}

function configureSignalRPushes (): void {
    signalRLifecycle.joinGroup('module', `EpexSlideout${contract.value?.contractId}`);
    signalRLifecycle.listenTo('epexNewSlideout', refreshHandler);
    signalRLifecycle.listenTo('epexSlideoutUpdateTrade', tradeHandler);
    signalRLifecycle.listenTo('epexSlideoutUpdateBidAsk', bidAskHandler);
}

async function handleSignalRPush (pushType: PushTypes, newId: number = props.contractId): Promise<void> {
    if (newId === props.contractId) {
        switch (pushType) {
            case PushTypes.Refresh:
                fetchSlideoutData(RequestKey.Initial, (data: ITradeContract) => {
                    contract.value = { ...data, metrics: getMetricsWithFallback(data.metrics) };
                    setDeliveryDate();
                    setModuleChartConfig(data.chartConfig);
                });
                break;
            case PushTypes.Trade:
                fetchSlideoutData(RequestKey.Trade, (data: ITradeContract) => {
                    setTradeProperties(data);
                });
                break;
            case PushTypes.BidAsk:
                fetchSlideoutData(RequestKey.BidAsk, (data: ITradeContract) => {
                    setBidAskProperties(data);
                });
                break;
        }
    }
}

function setTradeProperties (newContract: ITradeContract): void {
    if (!contract.value) return;
    contract.value.noData = newContract.noData;

    contract.value.tradeTable = newContract.tradeTable;
    contract.value.chartData = newContract.chartData;

    setModuleChartConfig(newContract.chartConfig);
}

function setBidAskProperties (newContract: ITradeContract): void {
    if (!contract.value) return;
    contract.value.noData = newContract.noData;

    contract.value.tradeTable = newContract.tradeTable;
    contract.value.chartData = newContract.chartData;
    contract.value.metrics = getMetricsWithFallback(newContract.metrics);

    contract.value.bidTable = newContract.bidTable;
    contract.value.askTable = newContract.askTable;
}

function unsubscribeFromModule (): void {
    signalRLifecycle.leaveGroup('module', `EpexSlideout${contract.value?.contractId}`);
    signalRLifecycle.endListen('epexNewSlideout', refreshHandler);
    signalRLifecycle.endListen('epexSlideoutUpdateTrade', tradeHandler);
    signalRLifecycle.endListen('epexSlideoutUpdateBidAsk', bidAskHandler);
}

function resetRequestState (): void {
    requestState.value = {
        [RequestKey.Initial]: { pending: false, rerun: false },
        [RequestKey.Trade]: { pending: false, rerun: false },
        [RequestKey.BidAsk]: { pending: false, rerun: false },
    };
}

async function getContractDataById (contractId: number): Promise<ITradeContract> {
    return (await epexApi.getContractDataById(contractId, props.countryId)).data;
}

function withSingleFlight (key: RequestKey, task: () => Promise<void>, hooks: { onStart?: () => void; onFinally?: () => void } = {}): Promise<void> {
    const state = requestState.value[key];
    if (!state) return task();

    if (state.pending) {
        state.rerun = true;
        return Promise.resolve();
    }

    const { onStart, onFinally } = hooks;

    state.pending = true;
    state.rerun = false;
    onStart?.();

    const finalize = (): void => {
        state.pending = false;
        onFinally?.();
        if (state.rerun && isAlive.value) {
            state.rerun = false;

            nextTick(() => {
                if (!isAlive.value) return;
                withSingleFlight(key, task, hooks);
            });
        }
    };

    return Promise.resolve()
        .then(() => task())
        .finally(finalize);
}

function fetchSlideoutData (key: RequestKey, applyData: (data: ITradeContract) => void, options: { onError?: (error: unknown) => void; onStart?: () => void; onFinally?: () => void } = {}): void {
    const { onError, onStart, onFinally } = options;

    withSingleFlight(key, async () => {
        try {
            const data = await getContractDataById(props.contractId);
            if (!isAlive.value) return;
            applyData(data);
        } catch (error) {
            if (!isAlive.value) return;
            onError?.(error);
        }
    }, { onStart, onFinally });
}

async function initialise (): Promise<void> {
    fetchSlideoutData(RequestKey.Initial, (data: ITradeContract) => {
        contract.value = { ...data, metrics: getMetricsWithFallback(data.metrics) };
        setDeliveryDate();
        setModuleChartConfig(data.chartConfig);
        configureSignalRPushes();
    }, {
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'There was an error fetching this contract.',
                life: 3000,
            });
        },
        onStart: () => {
            isLoading.value = true;
        },
        onFinally: () => {
            isLoading.value = false;
        },
    });

    frontendLogApi.postFrontendEvent('Accessed Epex Grid (Pop-out)');
}

initialise();

watch(() => props.contractId, () => {
    unsubscribeFromModule();
    resetRequestState();
    initialise();
});

onUnmounted(() => {
    isAlive.value = false;
});

</script>

<style scoped lang="scss">
$gridRowHeight: 200px;

.loader {
    height: 100%;
    min-height: 0;
    align-content: center;
}

.epex-header {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: center;

    .metric-holder {
        display: flex;
        gap: var(--smallSpacing);
        padding-right: var(--extraLargeSpacing);
    }
}

.contracts-data {
    height: 100%;
    min-height: 0;
    overflow: auto;
    padding: 0 var(--defaultSpacing);

    .table-shell {
        height: 100%;
        min-height: $gridRowHeight;
        max-height: 100%;
        display: grid;
        overflow: hidden;
        padding: var(--defaultSpacing);
        background-color: var(--neutral0);
        border: solid 1px var(--neutral300);
        border-radius: var(--borderRadiusOuter);

        :deep(.table-outer-wrapper) {
            height: 100%;
            min-height: 0;
            overflow: hidden;
        }

        :deep(.table-wrapper) {
            min-height: 0;
            overflow: auto;
        }
    }

    .no-data {
        height: 100%;
        min-height: 0;
        text-align: center;
        font-size: var(--extraLargeFontSize);
        align-content: center;
    }

    .grid-row-cell {
        min-height: 0;
        max-height: 100%;
    }

    .data-holder {
        display: grid;
        grid-template-columns: repeat(6, 1fr);
        grid-template-rows: minmax($gridRowHeight, 2fr) minmax($gridRowHeight, 1.2fr) minmax($gridRowHeight, 1fr);
        gap: var(--largeSpacing);
        padding: var(--defaultSpacing) 0 var(--largeSpacing) 0;
        height: 100%;
        min-height: 0;
        max-height: 100%;

        .title {
            font-size: var(--largeFontSize);
            text-align: center;
        }

        .trade-chart-shell {
            grid-column: 1 / -1;
            grid-row: 1;
            height: 100%;
            overflow: hidden;

            :deep(.module-wrapper) {
                height: 100%;
                min-height: 0;
                overflow: hidden;
            }
        }

        .trade-table {
            grid-column: 1 / 5;
            grid-row: 2;
            min-width: 0;
            overflow: hidden;
        }

        .trade-metrics {
            grid-column: 5 / 7;
            grid-row: 2;
            min-width: 0;
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: 1fr 1fr;
            gap: var(--defaultSpacing);
            align-content: start;
            overflow: auto;

            > * {
                min-width: 0;
                padding: var(--smallSpacing) var(--largeSpacing);
                text-align: start;

                :deep(.metric-label) {
                    font-size: var(--extraSmallFontSize)!important;
                }

                :deep(.metric-value) {
                    font-size: var(--largeFontSize)!important;
                }
            }
        }

        .bids {
            grid-column: 1 / 4;
            grid-row: 3;
            overflow: hidden;
            display: grid;
            grid-template-rows: auto minmax(0, 1fr);
            gap: var(--largeSpacing);
            grid-template-areas:
                'bids-title'
                'bids-table';

            .bids-title {
                grid-area: bids-title;
            }

            .bids-table {
                grid-area: bids-table;
                min-height: 0;
            }
        }

        .asks {
            grid-column: 4 / 7;
            grid-row: 3;
            overflow: hidden;
            display: grid;
            grid-template-rows: auto minmax(0, 1fr);
            gap: var(--largeSpacing);
            grid-template-areas:
                'asks-title'
                'asks-table';

            .asks-title {
                grid-area: asks-title;
            }

            .asks-table {
                grid-area: asks-table;
                min-height: 0;
            }
        }

        @include responsive(sm) {
            grid-template-columns: 1fr;
            grid-template-rows:
                minmax($gridRowHeight, 1fr)
                minmax($gridRowHeight, 1fr)
                minmax($gridRowHeight, 1fr)
                minmax($gridRowHeight, 1fr)
                minmax($gridRowHeight, 1fr);

            .trade-chart-shell {
                grid-column: 1 / -1;
                grid-row: 1;
            }

            .trade-table {
                grid-column: 1 / -1;
                grid-row: 2;
            }

            .trade-metrics {
                grid-column: 1 / -1;
                grid-row: 3;
            }

            .bids {
                grid-column: 1 / -1;
                grid-row: 4;
            }

            .asks {
                grid-column: 1 / -1;
                grid-row: 5;
            }
        }
    }
}

</style>
